import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle
} from 'reactstrap';

const MedicalHistoryModal = ({ isOpen, toggle, onSave }) => {
  const [formData, setFormData] = useState({
    // A) Historial de Salud (checkboxes)
    healthIssues: {
      backProblems: false,
      neckProblems: false,
      shoulderProblems: false,
      hipProblems: false,
      kneeProblems: false,
      otherJointProblems: false,
      arthritis: false,
      hypertension: false,
      hypotension: false,
      glaucoma: false,
      diabetes: false,
      asthma: false,
      epilepsy: false,
      osteoporosis: false,
      pregnant: false
    },
    // Preguntas adicionales de salud
    surgeryLastTwoYears: '',
    otherMedicalIssues: '',
    medicalIssuesDetails: '',
    
    // B) Preguntas adicionales
    workSportActivities: [],
    firstTimePilates: '',
    previousPilatesExperience: '',
    previousPilatesLocation: '',
    otherPilatesLocation: '',
    pregnantLastSixMonths: '',
    hasChildren: '',
    hadCsection: '',
    dizzinessOrBalance: '',
    painfulMovements: '',
    referredByDoctor: '',
    doctorName: '',
    doctorPhone: '',
    
    // C) Objetivos
    reasonsForPilates: '',
    shortTermGoals: '',
    longTermGoals: '',
    timeInvestment: '',
    
    // D) Footer
    signature: '',
    date: ''
  });

  // Cargar datos del localStorage al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const savedData = localStorage.getItem('medicalHistoryData');
      if (savedData) {
        setFormData(JSON.parse(savedData));
      }
    }
  }, [isOpen]);

  // Guardar en localStorage cada vez que cambie el formulario
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem('medicalHistoryData', JSON.stringify(formData));
    }
  }, [formData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name.startsWith('healthIssues.')) {
        const healthIssue = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          healthIssues: {
            ...prev.healthIssues,
            [healthIssue]: checked
          }
        }));
      } else if (name.startsWith('workSportActivities.')) {
        const activity = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          workSportActivities: checked 
            ? [...prev.workSportActivities, activity]
            : prev.workSportActivities.filter(item => item !== activity)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = () => {
    // Guardar en localStorage
    localStorage.setItem('medicalHistoryData', JSON.stringify(formData));
    
    // Llamar callback del padre si existe
    if (onSave) {
      onSave(formData);
    }
    
    // Cerrar modal
    toggle();
  };

  const handleCancel = () => {
    // Limpiar localStorage si se cancela
    localStorage.removeItem('medicalHistoryData');
    toggle();
  };

  const healthIssuesList = [
    { key: 'backProblems', label: 'Problemas de Espalda' },
    { key: 'neckProblems', label: 'Problemas de Cuello' },
    { key: 'shoulderProblems', label: 'Problemas de Hombro' },
    { key: 'hipProblems', label: 'Problemas de Cadera' },
    { key: 'kneeProblems', label: 'Problemas de Rodilla' },
    { key: 'otherJointProblems', label: 'Otros Problemas de Articulaciones (muñecas, tobillos, codos…)' },
    { key: 'arthritis', label: 'Artritis' },
    { key: 'hypertension', label: 'Hipertensión (presión sanguínea alta)' },
    { key: 'hypotension', label: 'Hipotensión (presión sanguínea baja)' },
    { key: 'glaucoma', label: 'Glaucoma' },
    { key: 'diabetes', label: 'Diabetes o problemas de niveles bajos de azúcar en la sangre' },
    { key: 'asthma', label: 'Asma' },
    { key: 'epilepsy', label: 'Epilepsia' },
    { key: 'osteoporosis', label: 'Osteoporosis u Osteopenia' },
    { key: 'pregnant', label: 'Estoy embarazada' }
  ];

  const workSportOptions = [
    { key: 'sitting', label: 'Sentarse por largos periodos' },
    { key: 'lifting', label: 'Levantar cargas pesadas' },
    { key: 'standing', label: 'Estar de pie' },
    { key: 'repetitive', label: 'Alguna otra acción repetitiva' }
  ];

  const previousPilatesOptions = [
    { value: 'home_dvd', label: 'En casa con un DVD' },
    { value: 'other_studio_mat', label: 'En otro estudio - Mat' },
    { value: 'other_studio_equipment', label: 'En otro estudio - Aparatos' },
    { value: 'other', label: 'Otro' }
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable className="medical-history-modal">
      <ModalHeader toggle={toggle}>
        Historial Médico - Cuestionario de Salud
      </ModalHeader>
      
      <ModalBody>
        <Form>
          {/* A) Historial de Salud */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">A) Historial de Salud</CardTitle>
              
              <Row>
                {healthIssuesList.map((issue) => (
                  <Col md="6" key={issue.key}>
                    <div className="form-check mb-2 d-flex align-items-start">
                      <input
                        type="checkbox"
                        className="form-check-input me-2 mt-1"
                        name={`healthIssues.${issue.key}`}
                        id={issue.key}
                        checked={formData.healthIssues[issue.key]}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label flex-grow-1" htmlFor={issue.key}>
                        {issue.label}
                      </label>
                    </div>
                  </Col>
                ))}
              </Row>

              <hr />
              
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>¿Alguna cirugía en los últimos 2 años?</Label>
                    <Input
                      type="select"
                      name="surgeryLastTwoYears"
                      value={formData.surgeryLastTwoYears}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>¿Alguna otra cuestión médica?</Label>
                    <Input
                      type="select"
                      name="otherMedicalIssues"
                      value={formData.otherMedicalIssues}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {(formData.surgeryLastTwoYears === 'si' || formData.otherMedicalIssues === 'si') && (
                <FormGroup>
                  <Label>Detalles adicionales:</Label>
                  <Input
                    type="textarea"
                    name="medicalIssuesDetails"
                    value={formData.medicalIssuesDetails}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Por favor, proporcione más detalles..."
                  />
                </FormGroup>
              )}
            </CardBody>
          </Card>

          {/* B) Preguntas adicionales */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">B) Preguntas Adicionales</CardTitle>
              
              <FormGroup>
                <Label>1. ¿Su trabajo/deporte implica alguno de los siguientes?</Label>
                <Row>
                  {workSportOptions.map((option) => (
                    <Col md="6" key={option.key}>
                      <div className="form-check mb-2 d-flex align-items-start">
                        <input
                          type="checkbox"
                          className="form-check-input me-2 mt-1"
                          name={`workSportActivities.${option.key}`}
                          id={option.key}
                          checked={formData.workSportActivities.includes(option.key)}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label flex-grow-1" htmlFor={option.key}>
                          {option.label}
                        </label>
                      </div>
                    </Col>
                  ))}
                </Row>
              </FormGroup>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>2. ¿Es su primera vez tomando clases de Pilates?</Label>
                    <Input
                      type="select"
                      name="firstTimePilates"
                      value={formData.firstTimePilates}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>3. Si no, ¿dónde ha tomado antes?</Label>
                    <Input
                      type="select"
                      name="previousPilatesLocation"
                      value={formData.previousPilatesLocation}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      {previousPilatesOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {formData.previousPilatesLocation === 'other' && (
                <FormGroup>
                  <Label>Especifique:</Label>
                  <Input
                    type="text"
                    name="otherPilatesLocation"
                    value={formData.otherPilatesLocation}
                    onChange={handleInputChange}
                    placeholder="Especifique dónde ha tomado Pilates..."
                  />
                </FormGroup>
              )}

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>4. ¿Ha estado embarazada en los últimos 6 meses?</Label>
                    <Input
                      type="select"
                      name="pregnantLastSixMonths"
                      value={formData.pregnantLastSixMonths}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>5. ¿Ha tenido niño/niños?</Label>
                    <Input
                      type="select"
                      name="hasChildren"
                      value={formData.hasChildren}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>6. Si lo ha hecho, ¿Ha tenido cesárea?</Label>
                    <Input
                      type="select"
                      name="hadCsection"
                      value={formData.hadCsection}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>7. ¿Experimenta mareos o pierde el equilibrio al hacer ejercicio?</Label>
                    <Input
                      type="select"
                      name="dizzinessOrBalance"
                      value={formData.dizzinessOrBalance}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>8. ¿Hay algún movimiento que le cause dolor?</Label>
                    <Input
                      type="select"
                      name="painfulMovements"
                      value={formData.painfulMovements}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>9. ¿Ha sido referido a Pilates por un profesional médico?</Label>
                    <Input
                      type="select"
                      name="referredByDoctor"
                      value={formData.referredByDoctor}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {formData.referredByDoctor === 'si' && (
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Nombre del médico:</Label>
                      <Input
                        type="text"
                        name="doctorName"
                        value={formData.doctorName}
                        onChange={handleInputChange}
                        placeholder="Nombre del médico..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Teléfono del médico:</Label>
                      <Input
                        type="text"
                        name="doctorPhone"
                        value={formData.doctorPhone}
                        onChange={handleInputChange}
                        placeholder="Teléfono del médico..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          {/* C) Objetivos */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">C) Objetivos</CardTitle>
              
              <FormGroup>
                <Label>Razones para empezar Pilates:</Label>
                <Input
                  type="textarea"
                  name="reasonsForPilates"
                  value={formData.reasonsForPilates}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Describa sus razones para empezar Pilates..."
                />
              </FormGroup>

              <FormGroup>
                <Label>Objetivos a corto plazo:</Label>
                <Input
                  type="textarea"
                  name="shortTermGoals"
                  value={formData.shortTermGoals}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="¿Cuáles son sus objetivos a corto plazo?"
                />
              </FormGroup>

              <FormGroup>
                <Label>Objetivos a largo plazo:</Label>
                <Input
                  type="textarea"
                  name="longTermGoals"
                  value={formData.longTermGoals}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="¿Cuáles son sus objetivos a largo plazo?"
                />
              </FormGroup>

              <FormGroup>
                <Label>¿Qué tiempo está dispuesto a invertir para conseguir sus objetivos?</Label>
                <Input
                  type="textarea"
                  name="timeInvestment"
                  value={formData.timeInvestment}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Describa el tiempo que está dispuesto a invertir..."
                />
              </FormGroup>
            </CardBody>
          </Card>

          {/* D) Footer - Firma y Fecha */}
          <Card>
            <CardBody>
              <CardTitle tag="h5">D) Firma y Fecha</CardTitle>
              
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Firma:</Label>
                    <Input
                      type="text"
                      name="signature"
                      value={formData.signature}
                      onChange={handleInputChange}
                      placeholder="Nombre completo del alumno..."
                    />
                  </FormGroup>
                </Col>
                
                <Col md="6">
                  <FormGroup>
                    <Label>Fecha:</Label>
                    <Input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button color="danger" outline onClick={handleCancel}>
          Cancelar
        </Button>
        <Button color="primary" outline onClick={handleSave}>
          Guardar y Continuar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MedicalHistoryModal;
